import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import Navigation from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import axios from "axios";
import {
  User,
  Mail,
  Phone,
  GraduationCap,
  BookOpen,
  Heart,
  FlaskConical,
  TrendingUp,
  Target,
  AlertTriangle,
  CheckCircle,
  XCircle,
  BarChart3,
  Calendar,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  ChevronRight,
  Award,
  Flame,
  History,
  Star,
  Edit,
  // REMOVED: Zap, CreditCard — no credit display
} from "lucide-react";

const SUBJECT_COLORS = {
  Anatomy:     { gradient: "from-blue-500 to-blue-600",    bg: "bg-blue-50",   text: "text-blue-700",   border: "border-blue-200"   },
  Physiology:  { gradient: "from-rose-500 to-rose-600",    bg: "bg-rose-50",   text: "text-rose-700",   border: "border-rose-200"   },
  Biochemistry:{ gradient: "from-purple-500 to-purple-600", bg: "bg-purple-50", text: "text-purple-700", border: "border-purple-200" },
};
const SUBJECT_ICONS = { Anatomy: BookOpen, Physiology: Heart, Biochemistry: FlaskConical };

const READINESS_CONFIG = {
  exam_ready:     { label: "Exam Ready",     color: "text-emerald-700 bg-emerald-100 border-emerald-300" },
  borderline_safe:{ label: "Borderline Safe",color: "text-amber-700 bg-amber-100 border-amber-300"   },
  needs_practice: { label: "Needs Practice", color: "text-orange-700 bg-orange-100 border-orange-300" },
  at_risk:        { label: "At Risk",        color: "text-red-700 bg-red-100 border-red-300"         },
  not_started:    { label: "Not Started",    color: "text-slate-700 bg-slate-100 border-slate-300"   },
};

const formatDate = (ds) => {
  if (!ds) return "—";
  return new Date(ds).toLocaleDateString("en-IN", {
    day: "numeric", month: "short", year: "numeric",
  });
};

const formatDateTime = (ds) => {
  if (!ds) return "—";
  return new Date(ds).toLocaleDateString("en-IN", {
    day: "numeric", month: "short",
    hour: "2-digit", minute: "2-digit",
  });
};

export default function StudentProfilePage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [stats, setStats]               = useState(null);
  const [subjects, setSubjects]         = useState([]);
  const [attempts, setAttempts]         = useState([]);
  const [subjectStats, setSubjectStats] = useState({});
  const [loading, setLoading]           = useState(true);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [statsRes, subjectsRes, attemptsRes] = await Promise.all([
        axios.get(`${API}/stats/dashboard`),
        axios.get(`${API}/subjects`),
        axios.get(`${API}/attempts?limit=50`),
      ]);
      setStats(statsRes.data);
      setSubjects(subjectsRes.data);
      setAttempts(attemptsRes.data);

      // Compute per-subject stats from attempt history
      const bySubject = {};
      for (const a of attemptsRes.data) {
        const subj = a.subject || "Unknown";
        if (!bySubject[subj]) bySubject[subj] = { count: 0, totalPct: 0 };
        bySubject[subj].count++;
        bySubject[subj].totalPct += (a.evaluation?.percentage || 0);
      }
      const computed = {};
      for (const [s, v] of Object.entries(bySubject)) {
        computed[s] = { count: v.count, avg: v.count ? Math.round(v.totalPct / v.count) : 0 };
      }
      setSubjectStats(computed);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load profile data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#2E3A59] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const readiness      = READINESS_CONFIG[stats?.readiness || "not_started"];
  const initials       = user?.name?.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2) || "S";
  const recentAttempts = attempts.slice(0, 8);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navigation role="student" />

      <main className="lg:ml-64 pt-16 lg:pt-0">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8 space-y-6">

          {/* ── HERO CARD ── */}
          <div className="bg-gradient-to-r from-[#2E3A59] to-[#1A2236] rounded-2xl p-6 lg:p-8 text-white">
            <div className="flex flex-col sm:flex-row sm:items-center gap-6">
              {/* Avatar */}
              <div className="w-20 h-20 rounded-2xl bg-white/20 flex items-center justify-center text-3xl font-bold text-white flex-shrink-0">
                {initials}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap mb-1">
                  <h1 className="text-2xl font-heading font-bold">{user?.name}</h1>
                  {readiness && (
                    <span className={`text-xs font-bold px-3 py-1 rounded-full border ${readiness.color}`}>
                      {readiness.label}
                    </span>
                  )}
                </div>
                <p className="text-slate-300 text-sm mb-3">
                  {user?.university || "RGUHS"} · Semester {user?.semester || 1}
                  {user?.college ? ` · ${user.college}` : ""}
                </p>
                <div className="flex flex-wrap gap-4">
                  {user?.email && (
                    <div className="flex items-center gap-1.5 text-slate-300 text-sm">
                      <Mail className="w-4 h-4" /> {user.email}
                    </div>
                  )}
                  {user?.phone && (
                    <div className="flex items-center gap-1.5 text-slate-300 text-sm">
                      <Phone className="w-4 h-4" /> {user.phone}
                    </div>
                  )}
                </div>
              </div>

              {/* REMOVED: Credits widget (was showing "N credits left" here) */}
            </div>
          </div>

          {/* ── QUICK STATS ── */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { label: "Total Attempted", value: stats?.total_attempted || 0,      icon: Target,    color: "text-blue-600 bg-blue-50"    },
              { label: "Avg Score",       value: `${stats?.average_score || 0}%`,  icon: TrendingUp, color: "text-emerald-600 bg-emerald-50" },
              { label: "This Week",       value: stats?.week_attempted || 0,        icon: Calendar,  color: "text-purple-600 bg-purple-50" },
              // CHANGED: "Credits Used" → "Readiness" — more useful, no credit number shown
              { label: "Readiness",
                value: stats?.total_attempted >= 5
                  ? { exam_ready: "Exam Ready", borderline_safe: "Borderline", needs_practice: "Needs Work", at_risk: "At Risk" }[stats?.readiness] ?? "—"
                  : "Warming up",
                icon: Award,
                color: "text-amber-600 bg-amber-50" },
            ].map(({ label, value, icon: Icon, color }) => (
              <div key={label} className="bg-white rounded-xl border border-slate-100 p-4">
                <div className={`w-10 h-10 rounded-xl ${color} flex items-center justify-center mb-3`}>
                  <Icon className="w-5 h-5" />
                </div>
                <p className="text-2xl font-bold text-[#2E3A59]">{value}</p>
                <p className="text-xs text-slate-500 mt-0.5">{label}</p>
              </div>
            ))}
          </div>

          {/* ── SUBJECT PERFORMANCE ── */}
          <div className="bg-white rounded-xl border border-slate-100 p-5 lg:p-6">
            <h2 className="font-semibold text-[#2E3A59] mb-5 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-[#C9A227]" />
              Subject Performance
            </h2>
            <div className="space-y-5">
              {subjects.map((subject) => {
                const col  = SUBJECT_COLORS[subject.name] || SUBJECT_COLORS.Anatomy;
                const Icon = SUBJECT_ICONS[subject.name] || BookOpen;
                const ss   = subjectStats[subject.name] || { count: 0, avg: 0 };
                const pct  = ss.avg;
                const bar  = pct >= 70 ? "bg-emerald-500" : pct >= 50 ? "bg-amber-500" : pct > 0 ? "bg-red-400" : "bg-slate-200";

                return (
                  <div key={subject.name}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${col.gradient} flex items-center justify-center`}>
                          <Icon className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-[#2E3A59]">{subject.name}</p>
                          <p className="text-xs text-slate-400">{ss.count} attempt{ss.count !== 1 ? "s" : ""} · {subject.question_count} Qs available</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`text-sm font-bold ${pct >= 70 ? "text-emerald-600" : pct >= 50 ? "text-amber-600" : pct > 0 ? "text-red-500" : "text-slate-400"}`}>
                          {ss.count > 0 ? `${pct}%` : "—"}
                        </span>
                      </div>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full ${bar} rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
              {subjects.length === 0 && (
                <p className="text-slate-400 text-sm text-center py-4">No subjects loaded yet.</p>
              )}
            </div>
          </div>

          {/* ── WEAK AREAS ── */}
          {stats?.repeated_misses?.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-100 p-5 lg:p-6">
              <h2 className="font-semibold text-[#2E3A59] mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                Recurring Weak Areas
                <span className="text-xs text-slate-400 font-normal ml-1">(Fix these to gain the most marks)</span>
              </h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {stats.repeated_misses.slice(0, 6).map((miss, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 bg-red-50 rounded-xl border border-red-100">
                    <div className="w-7 h-7 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-bold text-red-600">{miss.count}×</span>
                    </div>
                    <div>
                      <p className="text-xs font-mono font-semibold text-red-700">{miss.code}</p>
                      {miss.detail && <p className="text-xs text-red-600 mt-0.5 line-clamp-2">{miss.detail}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── RECENT ATTEMPTS ── */}
          <div className="bg-white rounded-xl border border-slate-100 p-5 lg:p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-semibold text-[#2E3A59] flex items-center gap-2">
                <History className="w-5 h-5 text-[#C9A227]" />
                Recent Attempts
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/attempts")}
                className="text-[#2E3A59] text-xs"
              >
                View all <ChevronRight className="w-3 h-3 ml-1" />
              </Button>
            </div>

            {recentAttempts.length > 0 ? (
              <div className="space-y-2">
                {recentAttempts.map((attempt, i) => {
                  const pct = attempt.evaluation?.percentage || 0;
                  const color    = pct >= 70 ? "text-emerald-600" : pct >= 50 ? "text-amber-600" : "text-red-500";
                  const bgColor  = pct >= 70 ? "bg-emerald-100" : pct >= 50 ? "bg-amber-100" : "bg-red-100";
                  return (
                    <div key={i} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors">
                      <div className={`w-9 h-9 rounded-xl ${bgColor} flex items-center justify-center flex-shrink-0`}>
                        {pct >= 70 ? (
                          <ArrowUpRight className={`w-4 h-4 ${color}`} />
                        ) : (
                          <ArrowDownRight className={`w-4 h-4 ${color}`} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#2E3A59] truncate">
                          {attempt.question_snapshot?.question_text?.substring(0, 60) || "Question"}…
                        </p>
                        <p className="text-xs text-slate-400 mt-0.5">
                          {attempt.subject} · {formatDateTime(attempt.student_answer?.submitted_at)}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className={`text-sm font-bold ${color}`}>{pct}%</p>
                        <p className="text-xs text-slate-400">
                          {attempt.evaluation?.marks_awarded ?? attempt.evaluation?.marks_awarded_total ?? 0}/
                          {attempt.evaluation?.marks_total ?? attempt.question_snapshot?.marks_total ?? "?"}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-10">
                <History className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                <p className="text-sm text-slate-400 mb-4">No attempts yet. Start practising!</p>
                <Button onClick={() => navigate("/practice")} className="bg-[#2E3A59] hover:bg-[#1A2236]">
                  <BookOpen className="w-4 h-4 mr-2" /> Go to Practice
                </Button>
              </div>
            )}
          </div>

          {/* ── ACCOUNT INFO ── */}
          <div className="bg-white rounded-xl border border-slate-100 p-5 lg:p-6">
            <h2 className="font-semibold text-[#2E3A59] mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-[#C9A227]" />
              Account Details
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { label: "Full Name",    value: user?.name },
                { label: "Email",        value: user?.email || "—" },
                { label: "Phone",        value: user?.phone || "—" },
                { label: "University",   value: user?.university || "RGUHS" },
                { label: "College",      value: user?.college || "—" },
                { label: "Semester",     value: `Semester ${user?.semester || 1}` },
                { label: "Account Type", value: (user?.account_type || "trial").charAt(0).toUpperCase() + (user?.account_type || "trial").slice(1) },
                { label: "Member Since", value: formatDate(user?.created_at) },
              ].map(({ label, value }) => (
                <div key={label} className="flex flex-col gap-0.5">
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">{label}</span>
                  <span className="text-sm font-medium text-[#2E3A59]">{value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ── QUICK ACTIONS ── */}
          <div className="grid sm:grid-cols-2 gap-4 pb-8">
            <Button
              onClick={() => navigate("/practice")}
              className="h-12 bg-[#2E3A59] hover:bg-[#1A2236] font-semibold"
            >
              <BookOpen className="w-4 h-4 mr-2" /> Go to Practice
            </Button>
            <Button
              onClick={() => navigate("/attempts")}
              variant="outline"
              className="h-12 border-[#2E3A59] text-[#2E3A59] font-semibold"
            >
              <History className="w-4 h-4 mr-2" /> View All Attempts
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}